import { useEffect } from 'react';
import { difference } from 'es-toolkit';
import { OfficialPublicQuiz } from '@/module/public/components/AsideFilter/OfficialPublicQuiz';
import { PublisherAccordion } from '@/module/public/components/AsideFilter/PublisherAccordion';
import { QuestionSizeAccordion } from '@/module/public/components/AsideFilter/QuestionSizeAccordion';
import { SchoolAccordionItems } from '@/module/public/components/AsideFilter/SchoolAccordion';
import { SubjectAccordionItems } from '@/module/public/components/AsideFilter/SubjectAccordion';
import { TemplateTypeAccordion } from '@/module/public/components/AsideFilter/TemplateTypeAccordion';
import { usePublicCategories, usePublicCategoryFilter, usePublicSubjectFilterSelection } from '@/module/public/hooks';
import { FilterAccordionId, getExpandedItemsForSubjectFilterSelection, useAccordionStorage } from '@/module/public/store/AccordionStore';
import { useCountryCode } from '@/shared/hooks';
import { Accordion } from '@/ui_v3/components/accordion';
export const FilterContent = () => {
  const {
    handleSelectedSubject,
    selectedGrades,
    selectedSubject,
    isSubSubjectSelected
  } = usePublicCategoryFilter();
  const {
    isKR
  } = useCountryCode();
  const {
    subjectGroups
  } = usePublicCategories();
  const {
    selectSubjectFilter
  } = usePublicSubjectFilterSelection();
  const {
    expandedItems,
    handleExpandItems
  } = useAccordionStorage();
  const handleAccordionToggleWithoutSubject = (newAccordionValues: string[]) => {
    const 제거하는케이스 = difference(expandedItems, newAccordionValues).filter(value => !value.includes(FilterAccordionId.SubjectGroup));
    const 추가하는_케이스 = difference(newAccordionValues, expandedItems).filter(value => !value.includes(FilterAccordionId.SubjectGroup));

    //  서브젝트 그룹을 제외한 변경사항
    if ([...제거하는케이스, ...추가하는_케이스].length > 0) {
      handleExpandItems(newAccordionValues);
      return true;
    }
    return false;
  };

  /**
   * 아코디언의 확장/축소 상태를 관리하는 함수
   *
   * @param newAccordionValues - 새로 선택된 아코디언 항목들의 ID 배열
   *
   * 주요 기능:
   * 1. 과목 그룹 선택 여부 확인
   * 2. 기존에 열려있던 과목 그룹과 새로 선택된 과목 그룹 처리
   * 3. 과목 선택 상태 업데이트
   */
  const handleExpandAccordion = (newAccordionValues: string[]) => {
    const hasWithoutSubjectAction = handleAccordionToggleWithoutSubject(newAccordionValues);
    if (hasWithoutSubjectAction) {
      return;
    }

    // 과목 그룹 포함 여부를 확인하는 유틸리티 함수
    const hasSubjectGroup = (values: string[]) => values.some(value => value.includes(FilterAccordionId.SubjectGroup));

    // 과목 그룹 ID를 찾는 유틸리티 함수
    const getSubjectGroupId = (values: string[]) => values.find(value => value.includes(FilterAccordionId.SubjectGroup));

    // 1. 새로운 선택에 과목 그룹이 없는 경우: 과목 선택 초기화
    if (!hasSubjectGroup(newAccordionValues)) {
      handleSelectedSubject('');
      handleExpandItems(newAccordionValues);
      return;
    }

    // 2. 현재 열려있는 과목 그룹 확인
    const currentSubjectGroup = getSubjectGroupId(expandedItems);

    // 3. 새로운 아코디언 상태 계산
    // - 이미 열린 과목 그룹이 있으면: 해당 그룹을 제외하고 새로운 선택 추가
    // - 없으면: 새로운 선택으로 대체
    const updatedAccordionValues = currentSubjectGroup ? Array.from(new Set([...expandedItems, ...newAccordionValues])).filter(item => item !== currentSubjectGroup) : newAccordionValues;

    // 4. 새로 선택된 과목 그룹 처리
    const newSubjectGroup = getSubjectGroupId(updatedAccordionValues);
    if (newSubjectGroup) {
      const subjectId = newSubjectGroup.split(FilterAccordionId.SubjectGroup)[1];
      selectSubjectFilter({
        subjectId,
        subjectGroupId: subjectId,
        shouldExpandSubjectGroup: true
      });
      return;
    }

    // 5. 최종 아코디언 상태 업데이트
    handleExpandItems(updatedAccordionValues);
  };
  useEffect(() => {
    const openAutoExpandSubjectGroup = () => {
      const subjectGroupId = selectedSubject && isSubSubjectSelected ? selectedSubject.slice(0, 2) : selectedSubject;
      if (subjectGroupId && expandedItems) {
        const hasOwnSubSubjects = Object.keys(subjectGroups[subjectGroupId]?.children ?? {}).some(childGroupId => childGroupId !== subjectGroupId);
        const nextExpandedItems = getExpandedItemsForSubjectFilterSelection({
          expandedItems,
          subjectGroupId,
          shouldExpandSubjectGroup: isSubSubjectSelected || hasOwnSubSubjects
        });
        const isAlreadySynced = expandedItems.length === nextExpandedItems.length && expandedItems.every((item, index) => item === nextExpandedItems[index]);
        if (!isAlreadySynced) {
          handleExpandItems(nextExpandedItems);
        }
      }
    };
    openAutoExpandSubjectGroup();
  }, [expandedItems, handleExpandItems, isSubSubjectSelected, selectedSubject, selectedGrades, subjectGroups]);
  return <div className={'no-scrollbar flex h-[90vh] max-h-[90vh] w-full flex-col overflow-y-scroll'} data-sentry-component="FilterContent" data-sentry-source-file="FilterContent.tsx">
      {isKR && <OfficialPublicQuiz />}
      <Accordion value={expandedItems} onValueChange={handleExpandAccordion} defaultValue={[FilterAccordionId.Grade, FilterAccordionId.Subject]} type="multiple" className="size-full divide-y border-alternative" data-sentry-element="Accordion" data-sentry-source-file="FilterContent.tsx">
        <SchoolAccordionItems data-sentry-element="SchoolAccordionItems" data-sentry-source-file="FilterContent.tsx" />
        <SubjectAccordionItems data-sentry-element="SubjectAccordionItems" data-sentry-source-file="FilterContent.tsx" />
        {isKR && <PublisherAccordion />}
        <TemplateTypeAccordion data-sentry-element="TemplateTypeAccordion" data-sentry-source-file="FilterContent.tsx" />
        <QuestionSizeAccordion data-sentry-element="QuestionSizeAccordion" data-sentry-source-file="FilterContent.tsx" />
      </Accordion>
    </div>;
};